import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  useDisclosure,
  useToast,
  Box,
  Flex,
  Text,
} from "@chakra-ui/react";
import EmployeeDataService from "../../../services/Employee";
import { getUserAccess } from "../../../utils/authUtils";
import { HrmsPageShell } from "../../../components/Hrms/HrmsPageShell";
import { HrmsTable } from "../../../components/Hrms/HrmsTable";
import { leaveTypeColor, StatusBadge } from "../../../components/Hrms/hrmsUi";

interface LeaveTypeRow {
  code: string;
  name: string;
  annualQuota: number;
  isPaid: boolean;
  isUnlimited: boolean;
  carryForwardAllowed: boolean;
  maxCarryForwardDays: number;
  advanceNoticeDays: number;
  isActive: boolean;
}

const emptyForm: LeaveTypeRow = {
  code: "",
  name: "",
  annualQuota: 0,
  isPaid: true,
  isUnlimited: false,
  carryForwardAllowed: false,
  maxCarryForwardDays: 0,
  advanceNoticeDays: 0,
  isActive: true,
};

export const LeaveTypeMaster = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const toast = useToast();
  const { canManageConfig } = getUserAccess();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeRow[]>([]);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState<LeaveTypeRow>(emptyForm);

  const fetchLeaveTypes = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getLeaveTypes(token, true);
      if (res.data.status === "success") {
        setLeaveTypes(res.data.data || []);
      }
    } catch (e: any) {
      console.error("Error fetching leave types:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const openEdit = (leaveType?: LeaveTypeRow) => {
    setForm(leaveType || emptyForm);
    setIsNew(!leaveType);
    onOpen();
  };

  const handleSave = async () => {
    if (!form.code || !form.name) {
      toast({
        position: "top",
        title: "Code and name are required",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      const res = isNew
        ? await EmployeeDataService.createLeaveType(token, form, userEmail)
        : await EmployeeDataService.updateLeaveType(
            token,
            form.code,
            form,
            userEmail,
          );

      if (res.data.status === "success") {
        toast({
          position: "top",
          title: `Leave type ${isNew ? "created" : "updated"}`,
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        onClose();
        fetchLeaveTypes();
      }
    } catch (e: any) {
      toast({
        position: "top",
        title: "Error",
        description: e.response?.data?.message || "Failed to save leave type.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  return (
    <HrmsPageShell
      title="Leave Policy Master"
      description="Leave types, yearly allowances and rules shown to employees in the Leave Policy"
      sectionKey="configuration"
      noPermission={!canManageConfig}
      isLoading={canManageConfig && isLoading}
      actions={
        canManageConfig ? (
          <Button
            size="sm"
            bg="#327171"
            color="white"
            _hover={{ bg: "#285e5e" }}
            onClick={() => openEdit()}
          >
            Add Leave Type
          </Button>
        ) : undefined
      }
    >
      <HrmsTable
        head={[
          "Leave type",
          "Allowance",
          "Paid",
          "Carry forward",
          "Advance notice",
          "Status",
          "",
        ]}
        isEmpty={leaveTypes.length === 0}
        emptyText="No leave types configured"
      >
        {leaveTypes.map((lt) => (
          <tr key={lt.code}>
            <td>
              <Flex align="center" gap={2.5}>
                <Box w="10px" h="10px" borderRadius="full" bg={`${leaveTypeColor(lt.code)}.400`} flexShrink={0} />
                <Box>
                  <Text fontSize="13px" fontWeight="600" m={0}>
                    {lt.name}
                  </Text>
                  <Text fontSize="11px" color="gray.500" m={0}>
                    {lt.code}
                  </Text>
                </Box>
              </Flex>
            </td>
            <td>
              <Text as="span" fontWeight="700">
                {lt.isUnlimited ? "No limit" : lt.annualQuota}
              </Text>
              {!lt.isUnlimited && (
                <Text as="span" color="gray.500" fontSize="12px">
                  {" "}days / year
                </Text>
              )}
            </td>
            <td>
              <Badge colorScheme={lt.isPaid ? "green" : "gray"} borderRadius="full" px={2.5} fontSize="10px">
                {lt.isPaid ? "Paid" : "Unpaid"}
              </Badge>
            </td>
            <td>
              {lt.carryForwardAllowed
                ? `Up to ${lt.maxCarryForwardDays} days`
                : <Text as="span" color="gray.400">Not allowed</Text>}
            </td>
            <td>
              {lt.advanceNoticeDays
                ? `${lt.advanceNoticeDays} days before`
                : <Text as="span" color="gray.400">Not required</Text>}
            </td>
            <td>
              <StatusBadge status={lt.isActive ? "active" : "inactive"} />
            </td>
            <td style={{ textAlign: "right" }}>
              <Button
                size="sm"
                variant="outline"
                borderColor="#327171"
                color="#327171"
                _hover={{ bg: "#327171", color: "white" }}
                onClick={() => openEdit(lt)}
              >
                Edit
              </Button>
            </td>
          </tr>
        ))}
      </HrmsTable>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isNew ? "Add Leave Type" : `Edit Leave Type — ${form.code}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isDisabled={!isNew}>
                <FormLabel>Code</FormLabel>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Annual Quota</FormLabel>
                <Input
                  type="number"
                  value={form.annualQuota}
                  isDisabled={form.isUnlimited}
                  onChange={(e) =>
                    setForm({ ...form, annualQuota: Number(e.target.value) })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Advance Notice (days)</FormLabel>
                <Input
                  type="number"
                  value={form.advanceNoticeDays}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      advanceNoticeDays: Number(e.target.value),
                    })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Max Carry Forward Days</FormLabel>
                <Input
                  type="number"
                  value={form.maxCarryForwardDays}
                  isDisabled={!form.carryForwardAllowed}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      maxCarryForwardDays: Number(e.target.value),
                    })
                  }
                />
              </FormControl>
              <FormControl display="flex" flexDirection="column" gap={2} pt={6}>
                <Checkbox
                  isChecked={form.isPaid}
                  onChange={(e) => setForm({ ...form, isPaid: e.target.checked })}
                >
                  Paid Leave
                </Checkbox>
                <Checkbox
                  isChecked={form.isUnlimited}
                  onChange={(e) =>
                    setForm({ ...form, isUnlimited: e.target.checked })
                  }
                >
                  Unlimited
                </Checkbox>
                <Checkbox
                  isChecked={form.carryForwardAllowed}
                  onChange={(e) =>
                    setForm({ ...form, carryForwardAllowed: e.target.checked })
                  }
                >
                  Allow Carry Forward
                </Checkbox>
                <Checkbox
                  isChecked={form.isActive}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.checked })
                  }
                >
                  Active
                </Checkbox>
              </FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg="#327171"
              color="white"
              _hover={{ bg: "#285e5e" }}
              onClick={handleSave}
              isLoading={isSaving}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </HrmsPageShell>
  );
};
